// Page-level chrome that every product card in a preview needs, but that no SECTION owns.
//
// Section-scoped card styling already has a home: `section.productStyleProps`, resolved by
// productCardStylesFrom. The bits here are different — they come from the PAGE config
// (homePageConfig.addToCartButton) or from the storefront settings (enableProductFavorites), so a
// section has nothing to say about them and cannot pass them down.
//
// A context rather than props because the cards sit five preview flavors deep
// (StorefrontSectionItemPreview renders featured / bundles / category / explicit / saved-search
// variants, each its own component) and threading two page-level values through all of them —
// and through StorefrontSection between — would touch every signature for values none of those
// layers actually use.
//
// The default is deliberately inert: a surface that does not provide this (the Search page editor,
// the style-panel examples) renders exactly as it did before.

import React from 'react';

export interface IStorefrontCardChrome {
    /**
     * Resolved inline style for the card's floating add-to-cart button, from
     * homePageConfig.addToCartButton. Mirrors the runtime, which resolves this once per page
     * (HomePage.tsx `homeAddToCartStyle`) and hands the same object to every card rather than
     * letting each section restyle it.
     */
    addToCartStyle?: React.CSSProperties;
    /**
     * Whether cards show the favorite heart — the storefront's `enableProductFavorites`.
     *
     * The runtime additionally hides the heart for guests (useFavorites folds in a signed-in
     * check), but the preview deliberately does not: the merchant is designing the page a
     * signed-in shopper sees, and hiding the heart because the ADMIN happens not to be a
     * storefront customer would misrepresent the page for everyone.
     */
    showFavorites?: boolean;
    /**
     * Chrome for a side-scrolling section (IStorefrontSection.scrollProducts): the left/right
     * chevron buttons (homePageConfig.scrollButton, applied to both) and the scrolling row itself
     * (homePageConfig.scrollbar — standard scrollbar CSS, e.g. scrollbarColor/scrollbarWidth).
     * Page-level for the same reason addToCartStyle is.
     */
    scrollButtonStyle?: React.CSSProperties;
    scrollbarStyle?: React.CSSProperties;
}

const StorefrontCardChromeContext = React.createContext<IStorefrontCardChrome>({});

export const useCardChrome = (): IStorefrontCardChrome => React.useContext(StorefrontCardChromeContext);

export const StorefrontCardChromeProvider: React.FC<IStorefrontCardChrome> = ({ addToCartStyle, showFavorites, scrollButtonStyle, scrollbarStyle, children }) => {
    const value = React.useMemo(
        () => ({ addToCartStyle, showFavorites, scrollButtonStyle, scrollbarStyle }),
        [addToCartStyle, showFavorites, scrollButtonStyle, scrollbarStyle]);
    return <StorefrontCardChromeContext.Provider value={value}>{children}</StorefrontCardChromeContext.Provider>;
};

export default StorefrontCardChromeContext;
